import { useState, useEffect } from 'react';
import IntroScreen from './components/IntroScreen';
import QuestionScreen from './components/QuestionScreen';
import GateScreen from './components/GateScreen';
import SendingScreen from './components/SendingScreen';
import ResultsScreen from './components/ResultsScreen';
import { COLORS, FONTS } from './styles/tokens';
import { track } from './lib/analytics';
import { recordStart } from './lib/api';

const INITIAL_GATE = { firstName: '', email: '', company: '', website: '', orgType: '' };

export default function App() {
  const [stage, setStage] = useState('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [gateData, setGateData] = useState(INITIAL_GATE);
  // Row ID returned by the lead-capture insert — needed by the SendingScreen
  // to tell the server which assessment to email.
  const [assessmentId, setAssessmentId] = useState(null);
  // Resolved email outcome ('sent' | 'sending' | 'unsent'), handed down to
  // ResultsScreen so EmailAck shows what actually happened.
  const [emailStatus, setEmailStatus] = useState(null);
  // The built PDF doc, so ResultsScreen's download button doesn't re-render it.
  const [reportDoc, setReportDoc] = useState(null);

  useEffect(() => {
    const postHeight = () => {
      try {
        window.parent.postMessage(
          { type: 'aevara_resize', height: document.documentElement.scrollHeight },
          '*'
        );
      } catch (_) {}
    };
    postHeight();
    window.addEventListener('load', postHeight);
    return () => window.removeEventListener('load', postHeight);
  }, [stage]);

  function handleSelect(qId, answerObj) {
    const next = { ...answers, [qId]: answerObj };
    setAnswers(next);
    track('question_answered', { questionId: qId, score: answerObj.score ?? 0 });
    if (currentQ < 13) {
      setCurrentQ(q => q + 1);
    } else {
      setStage('gate');
      track('gate_shown');
    }
  }

  function handleBack() {
    if (currentQ === 0) {
      setStage('intro');
    } else {
      setCurrentQ(q => q - 1);
    }
  }

  function restart() {
    track('retake_clicked');
    setStage('intro');
    setCurrentQ(0);
    setAnswers({});
    setGateData(INITIAL_GATE);
    setAssessmentId(null);
    setEmailStatus(null);
    setReportDoc(null);
    window.scrollTo(0, 0);
  }

  const stageLabel = {
    intro: 'Introduction — AI Readiness Assessment',
    questions: `Question ${currentQ + 1} of 14`,
    gate: 'Last step — enter your details to see your report',
    sending: 'Sending your AI Readiness Report',
    results: 'Your AI Readiness Report is ready',
  }[stage] || '';

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bgApp, fontFamily: FONTS.body }}>
      {/* Skip link for keyboard users */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Screen-reader stage announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">{stageLabel}</div>

      {stage === 'intro' && (
        <IntroScreen onStart={() => { setStage('questions'); track('assessment_started'); void recordStart(); }} />
      )}
      {stage === 'questions' && (
        <QuestionScreen
          currentQ={currentQ}
          answers={answers}
          onSelect={handleSelect}
          onBack={handleBack}
        />
      )}
      {stage === 'gate' && (
        <GateScreen
          answers={answers}
          gateData={gateData}
          setGateData={setGateData}
          onBack={() => { setCurrentQ(13); setStage('questions'); }}
          onComplete={(route, overallLevel, orgType, id) => {
            track('gate_submitted', { route, overallLevel, orgType });
            setAssessmentId(id || null);
            setStage('sending');
          }}
        />
      )}
      {stage === 'sending' && (
        <SendingScreen
          answers={answers}
          gateData={gateData}
          assessmentId={assessmentId}
          onView={(doc, status) => {
            setReportDoc(doc || null);
            setEmailStatus(status || null);
            setStage('results');
          }}
        />
      )}
      {stage === 'results' && (
        <ResultsScreen
          answers={answers}
          gateData={gateData}
          onRetake={restart}
          emailStatus={emailStatus}
          reportDoc={reportDoc}
        />
      )}
    </div>
  );
}
